import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";

type LogLevel = "error" | "warn" | "info";

interface LogEntry {
  level: LogLevel;
  message: string;
  stack_trace?: string;
  url?: string;
  user_agent?: string;
  user_id?: string;
  metadata?: Record<string, unknown>;
}

class Logger {
  private buffer: LogEntry[] = [];
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private readonly FLUSH_INTERVAL_MS = 10_000;
  private readonly MAX_BUFFER_SIZE = 50;

  constructor() {
    this.flushInterval = setInterval(() => this.flush(), this.FLUSH_INTERVAL_MS);

    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => this.flush());
    }
  }

  private createEntry(
    level: LogLevel,
    message: string,
    options?: { stack?: string; metadata?: Record<string, unknown> }
  ): LogEntry {
    return {
      level,
      message: message.substring(0, 2000),
      stack_trace: options?.stack?.substring(0, 5000),
      url: typeof window !== "undefined" ? window.location.href : undefined,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      metadata: options?.metadata,
    };
  }

  error(message: string, options?: { stack?: string; metadata?: Record<string, unknown> }) {
    const entry = this.createEntry("error", message, options);
    console.error(`[Logger] ${message}`, options?.metadata);
    // Forward to Sentry
    if (options?.stack) {
      const err = new Error(message);
      err.stack = options.stack;
      Sentry.captureException(err, { extra: options?.metadata });
    } else {
      Sentry.captureMessage(message, { level: "error", extra: options?.metadata });
    }
    this.buffer.push(entry);
    // Flush immediately for errors
    this.flush();
  }

  warn(message: string, options?: { metadata?: Record<string, unknown> }) {
    const entry = this.createEntry("warn", message, options);
    console.warn(`[Logger] ${message}`, options?.metadata);
    Sentry.captureMessage(message, { level: "warning", extra: options?.metadata });
    this.buffer.push(entry);
    if (this.buffer.length >= this.MAX_BUFFER_SIZE) this.flush();
  }

  info(message: string, options?: { metadata?: Record<string, unknown> }) {
    const entry = this.createEntry("info", message, options);
    console.info(`[Logger] ${message}`, options?.metadata);
    this.buffer.push(entry);
    if (this.buffer.length >= this.MAX_BUFFER_SIZE) this.flush();
  }

  async flush() {
    if (this.buffer.length === 0) return;

    const entries = [...this.buffer];
    this.buffer = [];

    try {
      // Try to get user id
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      const rows = entries.map((e) => ({
        ...e,
        user_id: userId || null,
      }));

      const { error } = await supabase.from("app_logs").insert(rows);

      if (error) {
        console.error("[Logger] Failed to flush logs:", error.message);
        // Re-add failed entries (up to limit)
        this.buffer.push(...entries.slice(0, this.MAX_BUFFER_SIZE));
      }
    } catch (err: unknown) {
      console.error("[Logger] Flush exception:", err);
      this.buffer.push(...entries.slice(0, this.MAX_BUFFER_SIZE));
    }
  }

  destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    this.flush();
  }
}

export const logger = new Logger();
